import { useAppTheme } from "../../../hooks/useAppTheme";
import React, { useEffect, useState } from "react";
import { View, Text, TextInput, Image, Alert } from "react-native";
import { ScrollView, TouchableOpacity } from "react-native-gesture-handler";
import { Button } from "react-native-paper";
import * as ImagePicker from "expo-image-picker";
import { useToast } from "react-native-toast-notifications";
import { useDispatch, useSelector } from "react-redux";
import { updateAdminUserAccount } from "../../../redux/dispatcher/adminDispatcher";
import { getFileFromS3, pushFileToS3 } from "../../../services/s3BucketService";

interface FileObject {
  fileName: string;
  fileUri: string;
  fileType: string;
}

const AdminAccountProfileSetting: React.FC = () => {
  const { colors, isDark } = useAppTheme();
  const { userId, img, fullName } = useSelector((state: any) => state.admin);

  const [name, setFullName] = useState(fullName || "");
  const [password, setPassword] = useState("");
  const [image, setImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<FileObject | null>(null);
  const toast = useToast();
  const dispatch = useDispatch<any>();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        if (img) {
          const fileData = await getFileFromS3(img, "admin_users");
          if (fileData?.imageUrl) {
            setFile({
              fileName: img,
              fileUri: fileData.imageUrl,
              fileType: img.substring(img.lastIndexOf(".") + 1),
            });
            setImage(fileData.imageUrl); // Ensure UI updates with S3 image
          }
        }
      } catch (error) {
        console.error("Error fetching profile image:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [img]);

  const selectImage = async () => {
    let result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      aspect: [1, 1],
      quality: 0.5,
    });

    if (!result.canceled && result.assets.length > 0) {
      const fileObj = result.assets[0];
      const newFile: FileObject = {
        fileName: fileObj.fileName || `profile_${new Date().getTime()}.jpg`,
        fileUri: fileObj.uri,
        fileType: fileObj.mimeType || "image/jpeg",
      };
      setFile(newFile);
      setImage(fileObj.uri); // Update preview immediately
    }
  };

  console.log(name, "name");

  const handleUpdateProfile = async () => {
    setLoading(true);
    try {
      let uploadedImageUrl = img; // Keep existing image by default

      // Upload only if a new file is selected
      if (file && file.fileUri !== img) {
        const uniqueFileName = `${new Date().getTime()}_${file.fileName}`;
        const s3Response = await pushFileToS3(
          "POST",
          file.fileUri,
          uniqueFileName,
          file.fileType,
          "admin_users"
        );

        if (s3Response) {
          uploadedImageUrl = uniqueFileName; // Update image path if upload is successful
          toast.show("Image uploaded successfully!", { type: "success" });
        } else {
          toast.show("Image upload failed.", { type: "danger" });
        }
      }

      // Prepare only the fields that need to be updated
      const credentials: Record<string, any> = { name, userId };

      if (password) credentials.password = password; // Include password if provided
      if (uploadedImageUrl !== img) credentials.img = uploadedImageUrl; // Include new image only if changed

      dispatch(updateAdminUserAccount(credentials));
      toast.show("Profile updated successfully!", { type: "success" });
    } catch (error) {
      toast.show("Failed to update profile.", { type: "danger" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ScrollView style={{ padding: 20, backgroundColor: colors.background }}>
      <View style={{ alignItems: "center", marginBottom: 20 }}>
        <TouchableOpacity onPress={selectImage}>
          <Image
            source={
              image ? { uri: image } : require("../../../assets/images/boy.png")
            }
            style={{
              width: 120,
              height: 120,
              borderRadius: 60,
              marginBottom: 10,
            }}
          />
          <Text style={{ color: colors.primary, marginTop: 10, fontWeight: "500" }}>Change Profile Picture</Text>
        </TouchableOpacity>
      </View>

      <Text style={{ fontSize: 16, fontWeight: "bold", marginBottom: 5, color: colors.text }}>
        Full Name
      </Text>
      <TextInput
        value={name}
        onChangeText={setFullName}
        placeholder="Enter full name"
        placeholderTextColor={colors.textMuted}
        style={{
          backgroundColor: colors.surface,
          color: colors.text,
          borderWidth: 1,
          borderColor: colors.borderSubtle,
          padding: 10,
          borderRadius: 8,
          marginBottom: 15,
        }}
      />

      <Text style={{ fontSize: 16, fontWeight: "bold", marginBottom: 5, color: colors.text }}>
        New Password (Optional)
      </Text>
      <TextInput
        value={password}
        onChangeText={setPassword}
        placeholder="Enter new password"
        placeholderTextColor={colors.textMuted}
        secureTextEntry
        style={{
          backgroundColor: colors.surface,
          color: colors.text,
          borderWidth: 1,
          borderColor: colors.borderSubtle,
          padding: 10,
          borderRadius: 8,
          marginBottom: 20,
        }}
      />

      <Button mode="contained" loading={loading} onPress={handleUpdateProfile}>
        Update Profile
      </Button>
    </ScrollView>
  );
};

export default AdminAccountProfileSetting;
